import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { buildRequestsFilter } from '@/modules/service-requests/shared/application-api';
import { useRequestFilters } from '@/modules/service-requests/shared/use-request-filters';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-requests/shared/components/applications-table';
import { useByodApplications, useChangeByodStatus } from '../../hooks/use-byod';
import { ByodApplication, ByodStatus } from '../../types/byod.types';
import { createByodRequestColumns } from '../byod-columns/byod-columns';
import { ByodSpecsDialog } from '../byod-specs-dialog/byod-specs-dialog';

/** Existing UILM keys for the confirmation of each status change. */
const CONFIRM_COPY: Record<ByodStatus, { title: string; description: string; confirm: string }> = {
  Approved: {
    title: 'APPROVE_BYOD',
    description: 'ARE_YOU_SURE_TO_APPROVE_BYOD',
    confirm: 'APPROVE',
  },
  Rejected: {
    title: 'REJECT_BYOD',
    description: 'ARE_YOU_SURE_TO_REJECT_BYOD',
    confirm: 'REJECT',
  },
  Pending: {
    title: 'REVERT_BYOD_TO_PENDING',
    description: 'ARE_YOU_SURE_TO_REVERT_BYOD_TO_PENDING',
    confirm: 'REVERT_TO_PENDING',
  },
};

export const ByodRequests = () => {
  const { t } = useTranslation();
  const canReview = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.BYOD_REVIEW);
  // The name and department are filtered from their columns; this keeps the department scope.
  const { filters, departmentCondition, isScopeLoading } = useRequestFilters();
  const table = useApplicationsTable();
  const { resetPage } = table;
  const [specsTarget, setSpecsTarget] = useState<ByodApplication | null>(null);
  const [statusTarget, setStatusTarget] = useState<{
    application: ByodApplication;
    status: ByodStatus;
  } | null>(null);

  useEffect(() => resetPage(), [filters, resetPage]);

  const { data, isLoading, error } = useByodApplications({
    pageNo: table.pagination.pageIndex + 1,
    pageSize: table.pagination.pageSize,
    filter: mergeWhere(
      buildRequestsFilter(filters, 'FullName', departmentCondition),
      table.columnWhere
    ),
    order: table.order,
  });
  const { mutateAsync: changeStatus } = useChangeByodStatus();

  const columns = useMemo(
    () =>
      createByodRequestColumns({
        t,
        canReview,
        onViewSpecs: setSpecsTarget,
        onChangeStatus: (application, status) => setStatusTarget({ application, status }),
      }),
    [t, canReview]
  );

  const copy = statusTarget ? CONFIRM_COPY[statusTarget.status] : null;

  return (
    <div className="flex flex-col gap-4">
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading || isScopeLoading}
        error={error}
        table={table}
      />
      <ByodSpecsDialog application={specsTarget} onOpenChange={() => setSpecsTarget(null)} />
      <ConfirmationModal
        open={Boolean(statusTarget)}
        onOpenChange={(open) => !open && setStatusTarget(null)}
        title={copy ? t(copy.title) : ''}
        description={copy ? t(copy.description) : ''}
        confirmText={copy ? t(copy.confirm) : undefined}
        onConfirm={() =>
          statusTarget &&
          changeStatus({
            itemId: statusTarget.application.ItemId,
            status: statusTarget.status,
          })
        }
      />
    </div>
  );
};
